import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getPreferences, updatePreferences } from '../services/api';
import { useAuth } from './AuthContext';

// Default preference values — "show everything"
const DEFAULT_PREFERENCES = {
  dashboard: {
    showFiscalYear: true,
    showCompanyLabor: true,
    showLaborUtilization: true,
    showProjectOverview: true,
    defaultProjectView: 'kanban',
    projectsPerPage: 25,
  },
  projectDetail: {
    defaultTab: 'activity',
    expandedSections: ['optiportal', 'dropbox', 'sage', 'labor'],
    hiddenSections: [],
  },
  dispatch: {
    defaultDepartments: [],
    hiddenDepartments: [],
  },
  notifications: {
    emailDigest: true,
    taskReminders: true,
  },
  display: {
    compactMode: false,
    tableRowDensity: 'normal',
    theme: 'light',
  },
};

/**
 * Deep-merge `overrides` into `defaults`.
 * Only merges one level of nesting (top-level sections → key/value pairs).
 */
function mergeWithDefaults(defaults, overrides) {
  const result = { ...defaults };
  for (const [section, vals] of Object.entries(overrides)) {
    if (vals && typeof vals === 'object' && !Array.isArray(vals)) {
      result[section] = { ...(defaults[section] || {}), ...vals };
    } else {
      result[section] = vals;
    }
  }
  return result;
}

/**
 * Resolve a dot-notation path like "dashboard.showFiscalYear" into nested object access.
 */
function getByPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

function setByPath(obj, path, value) {
  const keys = path.split('.');
  const result = { ...obj };
  let cur = result;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return result;
}

const PreferencesContext = createContext(null);

export const usePreferences = () => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used within a PreferencesProvider');
  return ctx;
};

export const PreferencesProvider = ({ children }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [loaded, setLoaded] = useState(false);

  // Load preferences from server once user is authenticated
  useEffect(() => {
    if (!user) {
      setPreferences(DEFAULT_PREFERENCES);
      setLoaded(false);
      return;
    }

    let cancelled = false;
    getPreferences()
      .then((res) => {
        if (cancelled) return;
        const merged = mergeWithDefaults(DEFAULT_PREFERENCES, res.data || {});
        setPreferences(merged);
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load preferences:', err);
        setLoaded(true); // Fall through to defaults
      });

    return () => { cancelled = true; };
  }, [user]);

  /**
   * updatePreference('dashboard.showFiscalYear', false)
   * Immediately updates local state, then persists the top-level section to the API.
   */
  const updatePreference = useCallback(async (path, value) => {
    const updated = setByPath(preferences, path, value);
    setPreferences(updated);

    // Extract top-level section to send as a partial update
    const topKey = path.split('.')[0];
    try {
      await updatePreferences({ [topKey]: updated[topKey] });
    } catch (err) {
      console.error('Failed to save preference:', err);
    }
  }, [preferences]);

  /**
   * Get a preference value by dot-notation path, with fallback to default.
   */
  const getPreference = useCallback((path, fallback = undefined) => {
    const val = getByPath(preferences, path);
    return val !== undefined ? val : fallback;
  }, [preferences]);

  return (
    <PreferencesContext.Provider value={{ preferences, updatePreference, getPreference, loaded }}>
      {children}
    </PreferencesContext.Provider>
  );
};
